// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { BendTypeWithYBCRows, StrongOmit, WithRequiredNotNull } from '@evolvemep/api-ts';
import { Button, Divider, Flex, Stack, Text, TextInput } from '@mantine/core';

import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { StepperInput } from 'components/Mantine/StepperInput';
import { isNil } from 'helpers/isNotNil';
import { strongIsEqual } from 'helpers/strongEntries';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { BendDimensionInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendDimensionInputs';
import { BendMachineInfoInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendMachineInfoInputs';
import { getBendTypeWithYBCRows } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/helpers';
import {
  type BendCreatorFormValues,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';
import { BendCreatorShell } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/BendCreatorShell';
import { useBendCreatorPreviewData } from 'modules/Shop/EvolveFabrication/BendCreator/Shell/useBendCreatorPreviewData';

export type ValidatedFormValues = WithRequiredNotNull<
  BendCreatorFormValues,
  'benderProfile' | 'bendType' | 'conduitTypeId' | 'conduitSizeId' | 'partName' | 'quantity' | 'materialStockLength'
>;

export type ValidatedFormData = StrongOmit<ValidatedFormValues, 'bendType' | 'ybcRows'> & BendTypeWithYBCRows;

type BendCreatorFormProps = {
  onClose: () => void;
  isEditing?: boolean;
  onSubmit: (v: ValidatedFormData) => void;
};

type BendCreatorFormShellProps = {
  isEditing?: boolean;
  onClose: () => void;
  navPanel: ReactNode;
};

/** Hosts the form in the fullscreen shell. Split out so the preview hooks run inside `BendPreviewContextProvider`. */
const BendCreatorFormShell = ({ isEditing, onClose, navPanel }: BendCreatorFormShellProps) => {
  const {
    values: { bendType },
  } = useBendCreatorFormContext();
  const bendPreviewData = useBendCreatorPreviewData();
  return (
    <BendCreatorShell
      bendType={bendType}
      bendPreviewData={bendPreviewData}
      navPanel={navPanel}
      // EditBendModal keeps its own header with a close button until edit mode moves onto the shell.
      onClose={isEditing ? undefined : onClose}
      bendsToDiscard={0}
    />
  );
};

export const BendCreatorForm = ({ onClose, isEditing, onSubmit: parentOnSubmit }: BendCreatorFormProps) => {
  const form = useBendCreatorFormContext();

  const { lastValidation, isValidating } = useBendCreatorValidationContext();
  const { bendType: currentBendType, ybcRows: currentYbcRows } = form.values;
  const currentBendTypeWithRows = isNil(currentBendType)
    ? null
    : getBendTypeWithYBCRows({ bendType: currentBendType, ybcRows: currentYbcRows });
  // Rows only validate on blur, so pressing Enter in an edited cell would otherwise submit rows that were never checked.
  const validatedRowsAreCurrent = strongIsEqual(lastValidation?.request.ybcRows, currentBendTypeWithRows?.ybcRows);
  const serverSideValid = lastValidation?.isSuccess && !isValidating && validatedRowsAreCurrent;

  const handleSubmit = async (values: typeof form.values) => {
    // Validation ensures that all required fields will be non-null
    const { bendType, ybcRows, ...validatedValues } = values as ValidatedFormValues;
    const bendTypeWithRows = getBendTypeWithYBCRows({ bendType, ybcRows });
    if (!serverSideValid || isNil(bendTypeWithRows)) return;
    parentOnSubmit({ ...validatedValues, ...bendTypeWithRows });
  };

  return (
    <BendPreviewContextProvider>
      <BendCreatorFormShell
        isEditing={isEditing}
        onClose={onClose}
        navPanel={
          <form
            noValidate
            onSubmit={form.onSubmit(handleSubmit)}
            style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <Stack p="md" style={{ overflowY: 'auto' }}>
              <Flex justify="space-between">
                <Text size="xl" fw={500}>
                  Define Bend
                </Text>
                <Button variant="subtle" type="reset" onClick={form.reset}>
                  Reset form
                </Button>
              </Flex>
              <BendMachineInfoInputs />
              <Divider />
              <BendDimensionInputs />
              <Divider />
              <Stack gap={4}>
                <TextInput label="Part Name" disabled={isEditing} required {...form.getInputProps('partName')} />
                <StepperInput
                  mode="count"
                  label="Quantity"
                  disabled={isEditing}
                  required
                  min={1}
                  max={99}
                  step={1}
                  suffix=" ea"
                  {...form.getInputProps('quantity')}
                />
                {!isEditing && (
                  <Text c="dimmed" fz="sm">
                    Part name will be incremented when adding quantities over 1.
                  </Text>
                )}
              </Stack>
            </Stack>
            <ModalButtons
              type="submit"
              onClose={onClose}
              confirmationText={isValidating ? 'Validating bend data' : isEditing ? 'Update bend' : 'Add to work order'}
              disabled={!form.isValid() || !serverSideValid}
              loading={isValidating}
            />
          </form>
        }
      />
    </BendPreviewContextProvider>
  );
};
